import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  forwardRef,
  Injector,
  Input,
  OnInit,
  Output,
  signal,
  Signal,
  ViewChild,
  WritableSignal,
  inject,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormControlDirective,
  FormControlName,
  FormGroupDirective,
  NG_VALUE_ACCESSOR,
  NgControl,
  Validators,
} from '@angular/forms';
import { TextfieldValidation } from '@shared/abstraction/textfield-validation';
import { parseToTextfieldValidation } from '@shared/helpers/functions/parse-to-textfield-validation';
import { debounceTime, distinctUntilChanged, fromEvent, timer } from 'rxjs';

@Component({
  standalone: false,
  selector: 'gstar-textfield',
  templateUrl: './textfield.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextfieldComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TextfieldComponent
  implements ControlValueAccessor, OnInit, AfterViewInit
{
  private readonly injector = inject(Injector);

  @Input() label!: string;
  @Input() disabled!: boolean;
  @Input() maxlength = 524288;
  @Input() set messages(errorMessages: JSONObject) {
    this.#errorMessages = parseToTextfieldValidation(errorMessages, this.label);
  }
  @Input() showSuggestion: boolean = true;
  @Input() delay: number = 0;

  @Output() blurField: EventEmitter<Event> = new EventEmitter();
  @Output() focusField: EventEmitter<Event> = new EventEmitter();
  @Output() changeField: EventEmitter<string> = new EventEmitter();

  @ViewChild('field', { static: false })
  field!: {
    cdr: ChangeDetectorRef;
    hasLength: boolean;
  };
  @ViewChild('input', { static: false }) input!: ElementRef<HTMLInputElement>;

  value: WritableSignal<string> = signal('');
  valueLength: Signal<number> = computed(() =>
    this.value().length >= this.maxlength
      ? 0
      : this.maxlength - this.value().length
  );
  formControl!: FormControl;

  #errorMessages: TextfieldValidation[] = [];

  private onChange!: (value: string) => void;
  private onTouched!: () => void;

  get errors(): string[] {
    return this.formControl && this.formControl.errors
      ? Object.keys(this.formControl.errors)
      : [];
  }

  get errorMessages(): TextfieldValidation | undefined {
    let errorMessage;
    if (this.formControl && this.formControl.errors) {
      Object.keys(this.formControl.errors).forEach((errorKey: string) => {
        errorMessage = this.#errorMessages.find(
          field => field.name === errorKey
        );
        if (errorMessage) {
          errorMessage.invalid =
            this.formControl.dirty || this.formControl.touched;
        }
      });
    }

    return errorMessage;
  }

  get isRequired(): boolean {
    return !!(
      this.formControl && this.formControl.hasValidator(Validators.required)
    );
  }

  ngOnInit(): void {
    const ngControl = this.injector.get(NgControl);

    if (ngControl instanceof FormControlName) {
      this.formControl = this.injector
        .get(FormGroupDirective)
        .getControl(ngControl);
    } else {
      this.formControl = (ngControl as FormControlDirective)
        .form as FormControl;
    }

    if (this.disabled) {
      this.formControl.disable();
    }
  }

  ngAfterViewInit(): void {
    if (this.input) {
      fromEvent(this.input.nativeElement, 'change')
        .pipe(distinctUntilChanged(), debounceTime(this.delay))
        .subscribe(() => {
          this.changeField.emit(this.value());
        });
    }
  }

  onInput(event: Event): void {
    const { target }: { target: EventTarget | null } = event;
    if (target) {
      const control = target as HTMLInputElement;
      this.value.set(control.value);
      this.onTouched();
      this.onChange(this.value());
    }
  }

  blurHandler(event: Event): void {
    this.blurField.emit(event);
  }

  focusHandler(event: Event): void {
    this.focusField.emit(event);
  }

  writeValue(value: string): void {
    if (value) {
      this.value.set(value);
      if (this.field) {
        this.field.hasLength = true;
      }
      timer(0).subscribe(() => {
        this.field.cdr.detectChanges();
      });
    } else {
      this.value.set('');
      if (this.field) {
        this.field.hasLength = false;
      }
      timer(0).subscribe(() => {
        this.field.cdr.detectChanges();
      });
    }
  }
  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
